"use client";

import { useEffect, useRef } from "react";

import {
  buildEcommerceEvent,
  buildItemListId,
  buildItemListName,
  createEcommercePayload,
  type EcommerceItemCategory,
  mapProductToItem,
  mapProductTypeToAnalyticsCategory,
} from "@/lib/analytics/ecommerce-analytics";

import { EVENTS } from "@/constants/analytics.constants";
import { useTagging } from "@/hooks/useTagging";

export function ShopViewItemList({
  products,
  category,
}: ShopViewItemListProps) {
  const [, setTag] = useTagging();
  const firedRef = useRef<string>("");

  useEffect(() => {
    const key = `${category}_${products.length}`;
    if (firedRef.current === key) return;
    if (products.length === 0) return;

    firedRef.current = key;

    const itemListId = buildItemListId(category);
    const itemListName = buildItemListName(category);

    const items = products.map((product, index) =>
      mapProductToItem(product, {
        index,
        category:
          category === "all"
            ? mapProductTypeToAnalyticsCategory(product.type, product.id)
            : mapCategorySlugToItemCategory(category),
      }),
    );

    const payload = createEcommercePayload(items, {
      item_list_id: itemListId,
      item_list_name: itemListName,
    });

    setTag(buildEcommerceEvent(EVENTS.VIEW_ITEM_LIST, payload), {
      reset: true,
    });
  }, [products, category, setTag]);

  return null;
}

function mapCategorySlugToItemCategory(slug: string): EcommerceItemCategory {
  switch (slug) {
    case "kits":
      return "kit";
    case "cameras":
      return "camera";
    case "sensors":
      return "sensor";
    case "accessories":
      return "accessory";
    case "life-safety":
      return "life_safety";
    case "smart-home":
      return "smart_home";
    default:
      return "camera";
  }
}

type Product = {
  id?: string;
  slug?: string;
  name?: string;
  type?: string | null;
  listPrice?: number | null;
  salesPrice?: number | null;
};

type ShopViewItemListProps = {
  products: Product[];
  category: string;
};
